import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, Card, Col, Row } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { Icon, ButtonTooltip } from '../../components';
import { sendAlert } from '../../actions/utils';
import { showSelectionProcessRequest } from '../../requests/selectionProcesses';
import { indexSelectionProcessObservationsRequest } from '../../requests/selectionProcessObservations';
import { showCandidatesRequest } from '../../requests/candidates';
import { camelCaseEmptyStringRecursive } from '../../services/utils';
import basicSelectionProcess from './basicSelectionProcess';
import basicCandidate from './candidate';

const Field = ({ label, value, md = 2 }) => (
  <Col xs={12} md={md} className="mb-3 mr-4">
    <div className="d-flex flex-column mb-3">
      <span className="type">{label}</span>
      <span className={`answer ${!value ? 'text-grey' : ''}`}>{value || 'No Aplica'}</span>
    </div>
  </Col>
);

const CandidateShow = () => {
  const { id: selectionProcessId, candidateId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const [activeAccordion, setActiveAccordion] = useState('personal');
  const [selectionProcess, setSelectionProcess] = useState(basicSelectionProcess);
  const [candidate, setCandidate] = useState(basicCandidate);
  const [observations, setObservations] = useState([]);
  const [loadingObs, setLoadingObs] = useState(false);

  const selectionProcessCandidateId = useMemo(() => {
    if (!selectionProcess?.selectionProcessStagesAttributes?.length) return null;
    const cid = Number(candidateId);
    for (const stage of selectionProcess.selectionProcessStagesAttributes) {
      const found = (stage.selectionProcessCandidates || []).find(spc => spc?.candidate?.id === cid);
      if (found) return found.id;
    }
    return null;
  }, [selectionProcess, candidateId]);

  useEffect(() => {
    if (!selectionProcessCandidateId) return;
    setLoadingObs(true);
    indexSelectionProcessObservationsRequest(selectionProcessId, selectionProcessCandidateId, {
      dispatch,
      successCallback: resp => setObservations(resp.data || []),
      failureCallback: () => dispatch(sendAlert({ kind: 'error', message: 'No se pudo cargar las observaciones.' })),
      callback: () => setLoadingObs(false)
    });
  }, [selectionProcessId, selectionProcessCandidateId, dispatch]);

  useEffect(() => {
    showSelectionProcessRequest(selectionProcessId, {
      dispatch,
      successCallback: response => setSelectionProcess(camelCaseEmptyStringRecursive(response.data)),
      failureCallback: () => dispatch(sendAlert({ kind: 'error', message: 'No se pudo cargar el proceso.' }))
    });
  }, [selectionProcessId, dispatch]);

  useEffect(() => {
    showCandidatesRequest(candidateId, {
      dispatch,
      successCallback: response => setCandidate(camelCaseEmptyStringRecursive(response.data)),
      failureCallback: () => dispatch(sendAlert({ kind: 'error', message: 'No se pudo cargar el candidato.' }))
    });
  }, [candidateId, dispatch]);

  const {
    birthdate,
    careers = [],
    countryName,
    email,
    gender,
    filesInfo = [],
    fullAddress,
    fullName,
    hired,
    languages = [],
    nationalIdentification,
    observation,
    otherCareer,
    parsedPhone,
    resumeInfo = {},
    translatedCivilState,
    translatedEducationLevel,
    translatedIdentificationType
  } = candidate || {};

  const handleBack = () => {
    if (window.history.length > 1) {
      history.goBack();
    } else {
      history.push(`/selection_processes/${selectionProcessId}`);
    }
  };

  return (
    <>
      <Row className="mb-1 mt-3 align-items-center">
        <Col xs="auto">
          <button
            type="button"
            className="btn btn-circle btn-circle-dark "
            onClick={handleBack}
            style={{ marginTop: '0.75rem', marginLeft: '-0.5rem' }}
          >
            <Icon icon="chevron-back" />
          </button>
        </Col>

        <Col xs={10}>
          <h2 className="mt-3 mb-1">{fullName || 'Candidato'}</h2>
          {hired && <span className="text-hired">Candidato Contratado</span>}
        </Col>
      </Row>

      <div className="yellow-wrapper mt-4">
        <Accordion className="my-2" activeKey={activeAccordion} onSelect={k => setActiveAccordion(k)}>
          <Col className={`${activeAccordion === 'personal' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
            <Accordion.Toggle
              as={Col}
              eventKey="personal"
              className="card-header-title d-flex justify-content-between align-items-center"
              onClick={() => setActiveAccordion(activeAccordion === 'personal' ? null : 'personal')}
              style={{ textAlign: 'right', cursor: 'pointer' }}
            >
              <span>Información Personal</span>
            </Accordion.Toggle>
          </Col>
          <Accordion.Collapse eventKey="personal">
            <Card>
              <Card.Body>
                <Row>
                  <Field label="Nombre Completo:" value={fullName} />
                  <Field label="Fecha de Nacimiento:" value={birthdate} />
                  <Field label="Género:" value={gender?.label} />
                  <Field label="Estado Civil:" value={translatedCivilState} />
                  <Field
                    label={`Identificación (${translatedIdentificationType || '—'}):`}
                    value={nationalIdentification}
                  />
                </Row>
              </Card.Body>
            </Card>
          </Accordion.Collapse>
        </Accordion>

        <Accordion className="my-2" activeKey={activeAccordion} onSelect={k => setActiveAccordion(k)}>
          <Col className={`${activeAccordion === 'contacto' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
            <Accordion.Toggle
              as={Col}
              eventKey="contacto"
              className="card-header-title d-flex justify-content-between align-items-center"
              onClick={() => setActiveAccordion(activeAccordion === 'contacto' ? null : 'contacto')}
              style={{ textAlign: 'right', cursor: 'pointer' }}
            >
              <span>Información de Contacto</span>
            </Accordion.Toggle>
          </Col>

          <Accordion.Collapse eventKey="contacto">
            <Card>
              <Card.Body>
                <Row>
                  <Field label="Email:" value={email} />
                  <Field label="Teléfono:" value={parsedPhone} />
                  <Field label="Dirección:" value={[fullAddress, countryName].filter(Boolean).join('. ')} md={6} />
                </Row>
              </Card.Body>
            </Card>
          </Accordion.Collapse>
        </Accordion>

        <Accordion className="my-2" activeKey={activeAccordion} onSelect={k => setActiveAccordion(k)}>
          <Col className={`${activeAccordion === 'academica' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
            <Accordion.Toggle
              as={Col}
              eventKey="academica"
              className="card-header-title d-flex justify-content-between align-items-center"
              onClick={() => setActiveAccordion(activeAccordion === 'academica' ? null : 'academica')}
              style={{ textAlign: 'right', cursor: 'pointer' }}
            >
              <span>Información Académica</span>
            </Accordion.Toggle>
          </Col>

          <Accordion.Collapse eventKey="academica">
            <Card>
              <Card.Body>
                <Row>
                  <Field label="Nivel Educacional:" value={translatedEducationLevel} />
                  <Field
                    label="Profesión u Oficio:"
                    value={careers?.length ? careers.map(c => c.label).join(', ') : ''}
                  />
                  {careers?.some(c => c.label === 'Otro') && (
                    <Field label="Otra Profesión u Oficio:" value={otherCareer} />
                  )}
                  <Field label="Idiomas:" value={languages?.length ? languages.map(l => l.label).join(', ') : ''} />
                </Row>
              </Card.Body>
            </Card>
          </Accordion.Collapse>
        </Accordion>

        {(filesInfo?.length > 0 || Object.keys(resumeInfo || {}).length > 0) && (
          <Accordion className="my-2" activeKey={activeAccordion} onSelect={k => setActiveAccordion(k)}>
            <Col className={`${activeAccordion === 'documentos' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
              <Accordion.Toggle
                as={Col}
                eventKey="documentos"
                className="card-header-title d-flex justify-content-between align-items-center"
                onClick={() => setActiveAccordion(activeAccordion === 'documentos' ? null : 'documentos')}
                style={{ textAlign: 'right', cursor: 'pointer' }}
              >
                <span>Documentos Adjuntos</span>
              </Accordion.Toggle>
            </Col>

            <Accordion.Collapse eventKey="documentos">
              <Card>
                <Card.Body>
                  <Row>
                    <Col md={12}>
                      {Object.keys(resumeInfo).length > 0 && (
                        <Row>
                          <Col xs="auto">
                            <ButtonTooltip
                              onClick={() => window.open(resumeInfo.fileUrl, '_blank', 'noopener', 'noreferrer')}
                              variant="circle-primary"
                              className="btn-circle"
                              text="Exportar"
                            >
                              <Icon icon="download-2" />
                            </ButtonTooltip>
                          </Col>
                          <Col xs={10} className="d-flex align-items-center">
                            <p className="mb-0">{resumeInfo.cleanFilename}</p>
                          </Col>
                        </Row>
                      )}
                      {filesInfo.map(file => (
                        <Row key={file.id}>
                          <Col xs="auto">
                            <ButtonTooltip
                              onClick={() => window.open(file.fileUrl, '_blank', 'noopener', 'noreferrer')}
                              variant="circle-primary"
                              className="btn-circle"
                              text="Exportar"
                            >
                              <Icon icon="download-2" />
                            </ButtonTooltip>
                          </Col>
                          <Col xs={2} className="d-flex align-items-center">
                            <p className="mb-0">{file.cleanFilename}</p>
                          </Col>
                        </Row>
                      ))}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Accordion.Collapse>
          </Accordion>
        )}

        <Accordion className="my-2" activeKey={activeAccordion} onSelect={k => setActiveAccordion(k)}>
          <Col className={`${activeAccordion === 'observaciones' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
            <Accordion.Toggle
              as={Col}
              eventKey="observaciones"
              className="card-header-title d-flex justify-content-between align-items-center"
              onClick={() => setActiveAccordion(activeAccordion === 'observaciones' ? null : 'observaciones')}
              style={{ textAlign: 'right', cursor: 'pointer' }}
            >
              <span>Observaciones Proceso de Selección</span>
            </Accordion.Toggle>
          </Col>

          <Accordion.Collapse eventKey="observaciones">
            <Card>
              <Card.Body>
                <Row>
                  <Col xs={12} md={12} className="mb-2">
                    <div
                      className="d-flex flex-column"
                      style={{ borderBottom: '1px solid #e6e6e6', marginBottom: '1rem' }}
                    >
                      <h4 className="mb-3 mt-1">Observación Inicial</h4>
                      <div className="answer">
                        {observation ? <p className="mt-2">{observation}</p> : 'Sin observaciones'}
                      </div>
                    </div>
                  </Col>
                </Row>

                <div className="mt-2">
                  {loadingObs && <div className="text-muted">Cargando observaciones…</div>}
                  {!loadingObs && (!observations || observations.length === 0) && (
                    <div className="text-muted">Sin observaciones registradas.</div>
                  )}

                  {!loadingObs &&
                    observations?.map((obs, idx) => (
                      <div key={obs.id || idx} style={{ borderBottom: '1px solid #e6e6e6', marginBottom: '1rem' }}>
                        <div>
                          <h4 className="mb-4 mt-2">{obs.stage || 'Sin etapa'}</h4>
                        </div>

                        <Row className="text-muted">
                          <Col md={3}>
                            <span className="my-3 mr-2">Responsable:</span>
                            <span className="font-weight-bold ">{obs.author.full_name || '—'}</span>
                          </Col>
                          <Col md={2}>
                            <span className="my-3 mr-2">Fecha:</span>
                            <span className="font-weight-bold">{obs.created_at}</span>
                          </Col>
                        </Row>

                        <div className="my-3">
                          <div className="text-muted ">Observaciones:</div>
                          <div className="font-weight-bold my-3">{obs.comment}</div>
                        </div>
                      </div>
                    ))}
                </div>
              </Card.Body>
            </Card>
          </Accordion.Collapse>
        </Accordion>
      </div>
    </>
  );
};

export default CandidateShow;
